<script>
  import Layout from '/components/Layout.svelte';
  import EventEditor from '/components/EventEditor.svelte';
  import { useForm, inertia, router, page } from '@inertiajs/svelte';

  export let type = '';
  export let content = '';
  export let relative_path = '';
  export let is_new = false;
  export let error = null;
  export let event_references = null;
  export let prev_file = null;
  export let next_file = null;

  $: flash = $page.props.flash || {};

  const form = useForm({
    content: content,
    relative_path: relative_path,
  });

  $: typeLabel = type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  $: title = is_new ? `New ${typeLabel} file` : `Edit: ${relative_path}`;

  $: useStructuredEditor = type === 'events';
  $: hasChain = event_references?.chain_peers?.length > 0;
  $: hasPicture = !!event_references?.picture;
  $: hasReferences = event_references && (
    hasPicture ||
    (event_references.outbound && event_references.outbound.length > 0) ||
    (event_references.inbound && event_references.inbound.length > 0) ||
    hasChain
  );

  const REFERENCE_LABELS = {
    enable_event: 'enables',
    disable_event: 'disables',
    trigger_event: 'triggers',
    schedule_event: 'schedules',
    skill_check_on_success: 'on success →',
    skill_check_on_failure: 'on failure →',
    skill_check_on_complication: 'on complication →',
    skill_check_on_critical_success: 'on crit success →',
    skill_check_on_critical_failure: 'on crit failure →',
    condition_event_triggered: 'requires triggered',
    condition_event_not_triggered: 'requires not triggered',
    condition_previous_event: 'requires previous',
    condition_player_choice: 'requires choice in',
  };

  function refLabel(type) {
    return REFERENCE_LABELS[type] || type.replace(/_/g, ' ');
  }

  function eventEditPath(path) {
    if (!path) return null;
    return `/editor/events/${path}`;
  }

  function save() {
    if (is_new) {
      $form.post(`/editor/${type}`, { preserveScroll: true });
    } else {
      $form.patch(`/editor/${type}/${relative_path}`, { preserveScroll: true });
    }
  }

  function confirmDelete() {
    if (confirm(`Delete ${relative_path}? This cannot be undone.`)) {
      router.delete(`/editor/${type}/${relative_path}`);
    }
  }

  function handleEditorChange(e) {
    $form.content = e.detail;
  }

  $: lineCount = ($form.content.match(/\n/g) || []).length + 1;
  $: charCount = $form.content.length;
</script>

<svelte:head>
  <title>{title} — Age of Trevor Webedit</title>
</svelte:head>

<Layout {title}>
  {#if flash.notice}
    <div class="flash flash-notice">{flash.notice}</div>
  {/if}

  {#if error || $form.errors.content || $form.errors.relative_path}
    <div class="flash flash-alert">
      {error || $form.errors.content || $form.errors.relative_path}
    </div>
  {/if}

  <form on:submit|preventDefault={save} class="editor-form">
    {#if is_new}
      <div class="field">
        <label for="relative_path">File path <span class="hint">(relative to {type}/, must end in .yml)</span></label>
        <input
          id="relative_path"
          type="text"
          bind:value={$form.relative_path}
          placeholder="my_file.yml or subdir/my_file.yml"
          class="path-input"
          disabled={$form.processing}
        />
      </div>
    {:else}
      <div class="path-display">
        <span class="path-label">File:</span>
        <span class="path-value">{type}/{relative_path}</span>
      </div>
    {/if}

    <div class="editor-meta">
      <span class="meta-stat">{lineCount} lines</span>
      <span class="meta-stat">{charCount} chars</span>
    </div>

    {#if useStructuredEditor}
      <EventEditor content={$form.content} on:change={handleEditorChange} />
    {:else}
      <textarea
        bind:value={$form.content}
        class="yaml-editor"
        spellcheck="false"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        disabled={$form.processing}
        placeholder="# YAML content here"
      ></textarea>
    {/if}

    <div class="action-bar">
      <div class="action-left">
        <a use:inertia href="/editor/{type}" class="btn-cancel">← Back</a>
        {#if !is_new}
          <button type="button" class="btn-delete" on:click={confirmDelete} disabled={$form.processing}>
            Delete
          </button>
        {/if}
      </div>
      {#if !is_new && prev_file && next_file}
        <div class="sibling-nav">
          <a use:inertia href="/editor/{type}/{prev_file.relative_path.replace(/\.yml$/, '')}" class="btn-sibling" title={prev_file.display}>← Prev</a>
          <a use:inertia href="/editor/{type}/{next_file.relative_path.replace(/\.yml$/, '')}" class="btn-sibling" title={next_file.display}>Next →</a>
        </div>
      {/if}
      <button type="submit" class="btn-save" disabled={$form.processing}>
        {$form.processing ? 'Saving…' : 'Save & commit'}
      </button>
    </div>
  </form>

  {#if hasReferences}
    <div class="references-panel">
      <h2 class="references-title">Event References</h2>

      {#if hasPicture}
        <div class="references-section picture-section">
          <h3 class="references-section-title">Picture</h3>
          <div class="picture-wrap">
            {#if event_references.picture.url}
              <a href={event_references.picture.images_path} use:inertia>
                <img class="picture-thumb" src={event_references.picture.url} alt={event_references.picture.group_path} />
              </a>
            {/if}
            <a use:inertia href={event_references.picture.images_path} class="ref-link picture-link">{event_references.picture.group_path}</a>
          </div>
        </div>
      {/if}

      {#if hasChain}
        <div class="references-section chain-section">
          <h3 class="references-section-title">
            Chain: {event_references.chain_info?.chain_id}
            <span class="chain-size">({event_references.chain_info?.size} events)</span>
          </h3>
          <ul class="references-list chain-list">
            {#each event_references.chain_peers as peer}
              <li class="reference-item">
                {#if peer.path}
                  <a use:inertia href={eventEditPath(peer.path)} class="ref-link">{peer.event_id}</a>
                {:else}
                  <span class="ref-id ref-missing" title="No file found for this event ID">{peer.event_id}</span>
                {/if}
              </li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if event_references.outbound && event_references.outbound.length > 0}
        <div class="references-section">
          <h3 class="references-section-title">References from this event</h3>
          <ul class="references-list">
            {#each event_references.outbound as ref}
              <li class="reference-item">
                <span class="ref-type">{refLabel(ref.reference_type)}</span>
                {#if ref.path}
                  <a use:inertia href={eventEditPath(ref.path)} class="ref-link">{ref.event_id}</a>
                {:else}
                  <span class="ref-id ref-missing" title="No file found for this event ID">{ref.event_id}</span>
                {/if}
              </li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if event_references.inbound && event_references.inbound.length > 0}
        <div class="references-section">
          <h3 class="references-section-title">Events that reference this event</h3>
          <ul class="references-list">
            {#each event_references.inbound as ref}
              <li class="reference-item">
                <a use:inertia href={eventEditPath(ref.path)} class="ref-link">{ref.event_id}</a>
                <span class="ref-type">{refLabel(ref.reference_type)}</span>
                <span class="ref-this">this event</span>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>
  {/if}
</Layout>

<style>
  .flash {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .flash-notice {
    background: #f0fdf4;
    border: 1px solid #86efac;
    color: #166534;
  }

  .flash-alert {
    background: #fef2f2;
    border: 1px solid #fca5a5;
    color: #dc2626;
  }

  .editor-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
  }

  .hint {
    font-weight: 400;
    color: #94a3b8;
    font-size: 0.8rem;
  }

  .path-input {
    padding: 0.5rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-family: 'Fira Code', monospace;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .path-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  .path-display {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
    padding: 0.375rem 0;
  }

  .path-label {
    font-weight: 600;
    color: #374151;
  }

  .path-value {
    font-family: 'Fira Code', monospace;
    color: #1d4ed8;
  }

  .editor-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.8rem;
    color: #94a3b8;
  }

  .meta-stat {
    font-family: 'Fira Code', monospace;
  }

  .yaml-editor {
    width: 100%;
    min-height: 60vh;
    padding: 1rem;
    font-family: 'Fira Code', monospace;
    font-size: 0.875rem;
    line-height: 1.6;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    resize: vertical;
    outline: none;
    background: #0f172a;
    color: #e2e8f0;
    caret-color: #93c5fd;
    tab-size: 2;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .yaml-editor:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  .yaml-editor:disabled {
    opacity: 0.6;
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding-top: 0.5rem;
  }

  .action-left {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .btn-cancel {
    color: #64748b;
    text-decoration: none;
    font-size: 0.9rem;
    padding: 0.5rem 0.875rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    transition: background 0.2s;
  }

  .btn-cancel:hover {
    background: #f8fafc;
  }

  .btn-delete {
    background: none;
    border: 1px solid #fca5a5;
    color: #dc2626;
    padding: 0.5rem 0.875rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-delete:hover:not(:disabled) {
    background: #fef2f2;
  }

  .btn-delete:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sibling-nav {
    display: flex;
    gap: 0.375rem;
  }

  .btn-sibling {
    color: #64748b;
    text-decoration: none;
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    transition: background 0.2s, color 0.2s;
    white-space: nowrap;
  }

  .btn-sibling:hover {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
  }

  .btn-save {
    background: #1d4ed8;
    color: white;
    border: none;
    padding: 0.625rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;
  }

  .btn-save:hover:not(:disabled) {
    background: #1e40af;
    transform: translateY(-1px);
  }

  .btn-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* References panel */
  .references-panel {
    margin-top: 2rem;
    padding: 1.25rem 1.5rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
  }

  .references-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 1rem 0;
  }

  .references-section {
    margin-bottom: 1rem;
  }

  .references-section:last-child {
    margin-bottom: 0;
  }

  .references-section-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.5rem 0;
  }

  .references-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .reference-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
  }

  .ref-type {
    font-size: 0.75rem;
    color: #94a3b8;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    font-family: 'Fira Code', monospace;
    white-space: nowrap;
  }

  .ref-link {
    font-family: 'Fira Code', monospace;
    color: #1d4ed8;
    text-decoration: none;
    font-size: 0.875rem;
  }

  .ref-link:hover {
    text-decoration: underline;
  }

  .ref-id {
    font-family: 'Fira Code', monospace;
    font-size: 0.875rem;
  }

  .ref-missing {
    color: #dc2626;
    text-decoration: line-through;
    cursor: help;
  }

  .ref-this {
    font-size: 0.75rem;
    color: #94a3b8;
  }

  .chain-section {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
  }

  .chain-section .references-section-title {
    color: #1d4ed8;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  .chain-size {
    font-size: 0.75rem;
    font-weight: 400;
    color: #60a5fa;
    text-transform: none;
    letter-spacing: 0;
  }

  .chain-list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .chain-list .reference-item {
    display: inline-flex;
  }

  .picture-section {
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
  }

  .picture-section .references-section-title {
    color: #166534;
  }

  .picture-wrap {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .picture-thumb {
    width: 80px;
    height: 60px;
    object-fit: cover;
    border-radius: 0.375rem;
    border: 1px solid #86efac;
  }

  .picture-link {
    color: #166534;
  }
</style>
